import { useEffect, useRef, RefObject } from 'react';

/**
 * Calls `callback` when a mousedown lands outside `ref`.
 *
 * The listener is only attached while `enabled` is true. Components that render this hook in
 * large lists (calendar cards mount several tooltips each) would otherwise keep a `document`
 * listener alive per instance for the whole time they are mounted, so every click in the app
 * has to walk thousands of handlers before anything else can happen.
 */
export const useClickOutsideChecker = (ref: RefObject<HTMLDivElement>, callback: (event: MouseEvent) => void, enabled = true) => {
  const callbackRef = useRef(callback);

  // Kept in a ref so a new callback identity on each render does not detach and reattach the listener.
  useEffect(() => {
    callbackRef.current = callback;
  });

  useEffect(() => {
    if (!enabled) return;

    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) callbackRef.current(event);
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [ref, enabled]);
};
